/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\SmsProviderBasicBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX SMS Messaging module.
----------------------------------------------------------------*/


import { Server } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Toggle } from '../../../components/ui/Toggle';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import Select from '../../../components/ui/select';
import { useI18n } from '../../../hooks/useI18n';
import { SmsProviderFormData } from '../types';
import { PROVIDER_OPTIONS } from '../smsProviderConstants';

interface Props {
    formData: SmsProviderFormData;
    errors: Record<string, string>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFieldChange: (field: keyof SmsProviderFormData, value: any) => void;
}

export function SmsProviderBasicBox({ formData, errors, onFieldChange }: Props) {
    const { t } = useI18n();
    const tt = (key: string, text: string) => (
        <SimpleTooltip content={t(key)} position="top">{text}</SimpleTooltip>
    );

    return (
        <FormBox title={t('sms.providers.basicInfo')} icon={<Server className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                {/* Name */}
                <SimpleTooltip content={t('sms.providers.fields.name')} position="top">
                    <Label htmlFor="name" required className="text-left">{t('sms.providers.fields.name')}</Label>
                </SimpleTooltip>
                <Input id="name" value={formData.name || ''} onChange={(e) => onFieldChange('name', e.target.value)} placeholder={t('sms.providers.fields.namePlaceholder')} error={errors.name} />

                {/* Provider Type */}
                <SimpleTooltip content={t('sms.providers.fields.type')} position="top">
                    <Label htmlFor="providerType" required className="text-left">{t('sms.providers.fields.type')}</Label>
                </SimpleTooltip>
                <Select value={formData.providerType} onValueChange={(v) => onFieldChange('providerType', v)} options={PROVIDER_OPTIONS} searchable />

                {/* Priority */}
                <SimpleTooltip content={t('sms.providers.fields.priority')} position="top">
                    <Label htmlFor="priority" className="text-left">{t('sms.providers.fields.priority')}</Label>
                </SimpleTooltip>
                <Input id="priority" type="number" value={formData.priority || 100} onChange={(e) => onFieldChange('priority', parseInt(e.target.value) || 100)} />

                {/* Rate Limit */}
                <SimpleTooltip content={t('sms.providers.fields.rateLimit')} position="top">
                    <Label htmlFor="rateLimitPerSecond" className="text-left">{t('sms.providers.fields.rateLimit')}</Label>
                </SimpleTooltip>
                <Input id="rateLimitPerSecond" type="number" value={formData.rateLimitPerSecond || 10} onChange={(e) => onFieldChange('rateLimitPerSecond', parseInt(e.target.value) || 10)} />

                {/* Is Default */}
                <SimpleTooltip content={t('sms.providers.fields.isDefault')} position="top">
                    <Label htmlFor="isDefault" className="text-left">{t('sms.providers.fields.isDefault')}</Label>
                </SimpleTooltip>
                <Toggle id="isDefault" value={formData.isDefault || false} onChange={(v) => onFieldChange('isDefault', v)} />

                {/* Enabled */}
                <SimpleTooltip content={t('sms.providers.fields.enabled')} position="top">
                    <Label htmlFor="enabled" className="text-left">{t('sms.providers.fields.enabled')}</Label>
                </SimpleTooltip>
                <Toggle id="enabled" value={formData.enabled !== false} onChange={(v) => onFieldChange('enabled', v)} />
            </div>
        </FormBox>
    );
}
